<script setup lang="ts">
import { computed, ComputedRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useClaimStore } from '@/store';
import { claimLegalStepByName, ClaimLegalStepName, legalSteps } from '@/utils/config/constants/claims-legal-steps';

import {
  CheckIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeOpenIcon,
  ScaleIcon,
  CurrencyEuroIcon,
  BuildingLibraryIcon,
  WalletIcon,
} from "@heroicons/vue/20/solid";
// import { BuildingLibraryIcon } from "@heroicons/vue/24/solid";
import { getColor, claimLegalStepById } from '@/utils/config/constants';
import { UserCircleIcon } from '@heroicons/vue/20/solid';

type Status = 'complete' | 'current' | 'upcoming';

interface Step {
  id: string;
  name: string;
  description: string;
  href: string;
  stepName: ClaimLegalStepName;
  icon: any,
  color: string;
}

function getSteps() : Step[] {
  
  const steps = {
    "CLAIM_VALIDATION": { name: 'Validation', stepName: "CLAIM_VALIDATION", icon: UserCircleIcon, color: getColor("CLAIM_VALIDATION")+ "-700" },
    "UPCLAIM": { name: 'Amiable', stepName: "UPCLAIM", icon: ChatBubbleLeftRightIcon, color: getColor("UPCLAIM")+ "-700" },
    "CONCILIATION": { name: 'Précontentieux', stepName: "CONCILIATION", icon: EnvelopeOpenIcon, color: getColor("CONCILIATION")+ "-600" },
    "COURT": { name: "Contentieux", stepName: "COURT", icon: BuildingLibraryIcon, color: getColor("COURT")+ "-700" },
    "WAITING_FOR_MONEY": { name: "Attente de paiement", stepName: "WAITING_FOR_MONEY", icon: CurrencyEuroIcon, color: getColor("WAITING_FOR_MONEY")+ "-700" },
    "MONEY_RECEIVED":  { name: "Paiement reçu", stepName: "MONEY_RECEIVED", icon: WalletIcon, color: getColor("MONEY_RECEIVED")+ "-700" },
  }

  const claimLegalStepIds = useClaimStore().claim.claimEvents.reduce((acc, claimEvent) => {
    if (!acc.includes(claimEvent.eventType.claimLegalStepId)) {
      acc.push(claimEvent.eventType.claimLegalStepId);
    }
    return acc;
  }, [] as number[]);
  const claimLegalStepNames = claimLegalStepIds.map(id => claimLegalStepById(id));

  const currentClaimStep = useClaimStore().claim?.claimLegalStep.name;
  if (currentClaimStep === "CLAIM_VALIDATION") {
    return [
      steps["CLAIM_VALIDATION"] as any,
      steps["UPCLAIM"],
      steps["CONCILIATION"],
    ]
  }
  if (currentClaimStep === "UPCLAIM" || currentClaimStep === "CONCILIATION" || currentClaimStep === "COURT") {
    return [
      steps["UPCLAIM"] as any,
      steps["CONCILIATION"],
      steps["COURT"],
    ]
  } else if (currentClaimStep === "WAITING_FOR_MONEY") {
    const isCourt = claimLegalStepNames.includes("COURT");
    const isConciliation = claimLegalStepNames.includes("CONCILIATION");

    const claimSteps = [
      steps["WAITING_FOR_MONEY"] as any,
      steps["MONEY_RECEIVED"],
    ]

    if (isCourt) return [steps["COURT"], ...claimSteps];
    if (isConciliation) return [steps["CONCILIATION"], ...claimSteps];
    else return [steps["UPCLAIM"], ...claimSteps];

  } else if (currentClaimStep === "BAILIFF") {

  }
  return [];
}

const steps = computed(() => getSteps());

const { claim } = storeToRefs(useClaimStore());

function getStatus(stepName: ClaimLegalStepName) : Status {
  const currentClaimStep = claim.value.claimLegalStep;
  const step = claimLegalStepByName(stepName);

  if (step.id < currentClaimStep.id) return 'complete';
  else if (step.id === currentClaimStep.id) return 'current';
  return 'upcoming';
}
</script>

<template>
  <div class="sm:border-t sm:border-b sm:border-gray-200">
    <nav aria-label="Progress">
      <ol role="list" class=" sm:flex sm:divide-y-0">
        <li v-for="(step, stepIdx) in steps" :key="step.name" class="relative sm:flex-1 sm:flex">

          <!-- Complete -->
          <!-- <a v-if="getStatus(step.stepName) === 'complete'" :href="step.href" class="flex items-center w-full cursor-default group">
            <span class="flex items-center px-6 py-2 text-sm font-medium">
              <span :class="`bg-${step.color}/80 group-hover:bg-${step.color}/75`" class="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-full">
                <HeroIcons :solid="true" :name="step.icon" class="w-5 h-5 text-white" aria-hidden="true" />
              </span>
              <span class="ml-4 text-sm font-medium text-gray-900/80">{{ step.name }}</span>
            </span>
          </a> -->
          <a v-if="getStatus(step.stepName) === 'complete'" :href="step.href" class="flex items-center w-full cursor-default group" :class="stepIdx === 1 ? 'mr-5': ''">
            <span class="flex items-center px-5 py-2 text-sm font-medium">
              <span :class="`bg-${step.color}/90 group-hover:bg-${step.color}`" class="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-full">
                <CheckIcon class="w-4 h-4 text-white" aria-hidden="true" />
              </span>
              <span class="ml-3 text-sm font-medium text-gray-900/80">{{ step.name }}</span>
            </span>
          </a>

          <!-- Current -->
          <a v-else-if="getStatus(step.stepName) === 'current'" :href="step.href" class="flex items-center px-5 py-2 text-sm font-medium cursor-default" :class="stepIdx === 1 ? 'mr-5': ''" aria-current="step">
            <span class="flex items-center justify-center flex-shrink-0 w-8 h-8 border-2 rounded-full" :class="`border-${step.color}`" >
              <!-- <span :class="getClassTextColor(step)">{{ step.id }}</span> -->
              <component :is="step.icon" :class="`text-${step.color}`" class="w-5 h-5" aria-hidden="true" />
              <!-- <LibraryIcon class="w-5 h-5" :class="getClassTextColor(step)" aria-hidden="true" /> -->
            </span>
            <span class="ml-3 text-sm font-medium" :class="`text-${step.color}`">{{ step.name }}</span>
          </a>

          <!-- Upcoming -->
          <a v-else :href="step.href" class="flex items-center group" :class="stepIdx === 1 ? 'mr-5': ''">
            <span class="flex items-center px-5 py-2 text-sm font-medium">
              <span class="flex items-center justify-center flex-shrink-0 w-8 h-8 border-2 border-gray-300 rounded-full group-hover:border-gray-400">
                <!-- <span class="text-gray-500 group-hover:text-gray-900">{{ step.id }}</span> -->
                <component :is="step.icon" class="w-5 h-5 text-gray-500 group-hover:text-gray-900" aria-hidden="true" />
              </span>
              <span class="ml-3 text-sm font-medium text-gray-500 group-hover:text-gray-900">{{ step.name }}</span>
            </span>
          </a>

          <template v-if="(stepIdx !== steps.length - 1)">
            <!-- Arrow separator for lg screens and up -->
            <div class="absolute top-0 right-0 hidden w-5 h-full sm:block" aria-hidden="true">
              <svg class="w-full h-full text-gray-300" viewBox="0 0 22 80" fill="none" preserveAspectRatio="none">
                <path d="M0 -2L20 40L0 82" vector-effect="non-scaling-stroke" stroke="currentcolor" stroke-linejoin="round" />
              </svg>
            </div>
          </template>
        </li>
      </ol>
    </nav>
  </div>
</template>
